import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Clock, Users, Award, ArrowRight, Play, CheckCircle2, Calendar, Shield, Target, Package, Zap, Palette, Wrench, Handshake, Crosshair } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import EditableFaqList from '../components/EditableFaqList';
import { mockCamps } from '../data/mock';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import CircuitDivider from '../components/CircuitDivider';
import IconBadge from '../components/IconBadge';
import SectionHeader from '../components/SectionHeader';
import StepCards from '../components/StepCards';
import FeatureCard from '../components/FeatureCard';
import TestimonialsSection from '../components/TestimonialsSection';
import CtaBanner from '../components/CtaBanner';
import { useTestimonials } from '../hooks/useTestimonials';
import EditableText, { CtaLabel } from '../components/EditableText';
import { BACKEND_URL, BTN_PRIMARY, BTN_OUTLINE, mediaSrc } from '../config/brand';
import { IMAGES, resolveStageImage } from '../config/images';
import { formatDateRange } from '../lib/slotDates';
import { formatDaysLabelLong } from '../lib/weekdays';
import { formatSlotTime } from '../components/PlanningSlots';

const STAGE_OFFER_SLUG = 'stage-vacances';

const STAGE_FORMULAS = [
  {
    id: 'demi-journee',
    price: 240,
    titleKey: 'stages.formulas.10h.title',
    title: 'Demi-journée',
    textKey: 'stages.formulas.10h.text',
    text: 'Idéal pour découvrir',
    points: [
      { text: '5 matinées de 2h', key: 'stages.formulas.demi-journee.point.1' },
      { text: 'Matériel fourni', key: 'stages.formulas.demi-journee.point.2' },
    ],
  },
  {
    id: 'journee',
    price: 80,
    titleKey: 'stages.formulas.day.title',
    title: 'Journée',
    textKey: 'stages.formulas.day.text',
    text: 'Une journée complète',
    points: [
      { text: '9h – 16h', key: 'stages.formulas.day.point.1' },
      { text: 'Matériel fourni', key: 'stages.formulas.day.point.2' },
    ],
  },
  {
    id: 'semaine',
    price: 320,
    popular: true,
    titleKey: 'stages.formulas.20h.title',
    title: 'Semaine complète',
    textKey: 'stages.formulas.20h.text',
    text: 'Pour approfondir',
    points: [
      { text: '5 journées encadrées', key: 'stages.formulas.semaine.point.1' },
      { text: 'Projet personnel inclus', key: 'stages.formulas.semaine.point.2' },
    ],
  },
];

function stageFormulaReserveUrl(durationId) {
  return `/reserver?type=stage&offer=${STAGE_OFFER_SLUG}&duration=${durationId}`;
}

const DEFAULT_INCLUDED = [
  'Matériel fourni',
  'Support pédagogique',
  'Projets à emporter',
  'Certificat de participation',
];

function mapStageSession(session) {
  if (!session?.start_date) return null;
  const daysLabel =
    formatDaysLabelLong(session.days_of_week) || session.days_label || '';
  const timeLabel =
    session.start_time && session.end_time
      ? `${formatSlotTime(session.start_time)} – ${formatSlotTime(session.end_time)}`
      : '';
  return {
    key: `${session.schedule_id || ''}:${session.start_date}:${session.start_time || ''}`,
    start_date: session.start_date,
    end_date: session.end_date || session.start_date,
    dateLabel: formatDateRange(session.start_date, session.end_date || session.start_date),
    daysLabel,
    timeLabel,
    available_spots: session.available_spots ?? 0,
    center_name: session.center_name || null,
  };
}

const mapApiStage = (stage) => ({
  id: stage.id,
  title: stage.name,
  ageRange: stage.age_range || 'Tous âges',
  description: stage.description,
  image: mediaSrc(resolveStageImage(stage)),
  location: stage.location || 'Nice, Cannes, Monaco, Pégomas',
  start_date: stage.start_date || null,
  end_date: stage.end_date || null,
  days_of_week: stage.days_of_week || null,
  days_label: formatDaysLabelLong(stage.days_of_week || stage.day_of_week) || stage.days_label || '',
  has_open_slots: Boolean(stage.has_open_slots),
  available_spots: stage.available_spots ?? 0,
  open_slots: stage.open_slots ?? 0,
  sessions: Array.isArray(stage.sessions)
    ? stage.sessions.map(mapStageSession).filter(Boolean)
    : [],
  priceOptions: [
    {
      duration: 'demi-journee',
      price: stage.duration_10h_price ?? 240,
      label: `Demi-journée - ${stage.duration_10h_price ?? 240}€ TTC`,
    },
    {
      duration: 'journee',
      price: stage.duration_day_price ?? 80,
      label: `Journée - ${stage.duration_day_price ?? 80}€ TTC`,
    },
    {
      duration: 'semaine',
      price: stage.duration_20h_price ?? 320,
      label: `Semaine complète - ${stage.duration_20h_price ?? 320}€ TTC`,
    },
  ],
  included: stage.benefits?.length ? stage.benefits : DEFAULT_INCLUDED,
  robotOption:
    stage.theme?.toLowerCase() === 'makeblock'
      ? {
          available: true,
          robots: [
            { name: 'mBot', price: 60, value: 89 },
            { name: 'Codey Rocky', price: 70, value: 99 },
          ],
        }
      : undefined,
});

/** Dedupe stages by theme, keeping the nearest upcoming session per theme. */
const groupStagesByTheme = (stages) => {
  const byTheme = new Map();
  for (const stage of stages) {
    const key = (stage.theme || stage.name || stage.id).toLowerCase();
    if (!byTheme.has(key)) {
      byTheme.set(key, stage);
    }
  }
  return [...byTheme.values()];
};

const STAGE_STEPS = [
  { icon: Calendar, number: 1, title: 'Jour 1-2 : Découverte', description: "Prise en main du matériel et premiers projets simples. Votre enfant apprend les bases en s'amusant." },
  { icon: Zap, number: 2, title: 'Jour 3-4 : Création', description: "Projets plus complexes et créatifs. Votre enfant laisse libre cours à son imagination !" },
  { icon: Target, number: 3, title: 'Jour 5 : Présentation', description: 'Projet final et présentation devant les autres. Fierté et confiance garanties !' },
];

const STAGE_SKILLS = [
  { icon: Wrench, variant: 'purple', title: 'Technique', description: 'Construction, programmation et électronique selon le thème' },
  { icon: Palette, variant: 'orange', title: 'Créativité', description: 'Expression libre et projets personnalisés' },
  { icon: Handshake, variant: 'purple', title: 'Collaboration', description: 'Travail en équipe et entraide' },
  { icon: Crosshair, variant: 'orange', title: 'Confiance', description: 'Fierté de créer et de présenter son projet' },
];

const STAGE_BENEFITS = [
  { icon: CheckCircle2, variant: 'purple', title: 'Formule flexible', description: 'Demi-journée, journée ou semaine complète. Tous les thèmes disponibles dans toutes les formules.' },
  { icon: Users, variant: 'orange', title: 'Petits groupes de 10 max', description: 'Encadrement optimal avec 1 formateur pour 10 enfants maximum. Chacun avance à son rythme.' },
  { icon: Award, variant: 'purple', title: 'Tout le matériel fourni', description: 'Robots, composants, ordinateurs... Tout est fourni ! Option robot à prix réduit pour Makeblock.' },
];

const stagesFAQ = [
  {
    question: "Mon enfant peut-il venir sans expérience ?",
    answer: "Absolument ! Nos stages sont adaptés à tous les niveaux, débutants comme confirmés. Nous créons des groupes homogènes selon l'âge et le niveau."
  },
  {
    question: "Les repas sont-ils inclus ?",
    answer: "Non, les repas ne sont pas inclus. Prévoyez un pique-nique pour votre enfant. Nous avons un espace dédié pour le déjeuner avec micro-ondes disponible."
  },
  {
    question: "Puis-je choisir plusieurs thèmes ?",
    answer: "Oui ! Votre enfant peut faire plusieurs stages pendant les vacances. Nous proposons des stages sur toutes les périodes de vacances scolaires."
  },
  {
    question: "L'option robot Makeblock, comment ça marche ?",
    answer: "Pour le stage Makeblock, vous pouvez acheter le robot à prix réduit (+60€ à +90€). Votre enfant le garde et peut continuer à programmer chez lui !"
  },
  {
    question: "Que faire si mon enfant est malade ?",
    answer: "Nous proposons un report sur un prochain stage ou un avoir valable 1 an sur présentation d'un certificat médical."
  },
];

const Stages = () => {
  const navigate = useNavigate();
  const [selectedDuration, setSelectedDuration] = useState("demi-journee");
  const [camps, setCamps] = useState(mockCamps);
  const testimonials = useTestimonials(3);

  const selectAndReserve = (durationId) => {
    setSelectedDuration(durationId);
    navigate(stageFormulaReserveUrl(durationId));
  };

  useEffect(() => {
    if (!BACKEND_URL) return;
    const loadStages = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/stages`);
        if (!res.ok) return;
        const data = await res.json();
        if (!Array.isArray(data) || data.length === 0) return;
        const mapped = groupStagesByTheme(data.map(mapApiStage));
        if (mapped.length > 0) setCamps(mapped);
      } catch {
        // fallback mock
      }
    };
    loadStages();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section - Landing Page Style */}
      <section className="relative pt-below-site-header pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[rgba(58,0,224,0.06)] via-white to-[rgba(250,176,58,0.08)]"></div>
        <div className="absolute top-20 left-10 w-72 h-72 bg-[rgba(58,0,224,0.1)] rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 bg-[rgba(250,176,58,0.14)] rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal animation="fade-up">
            <div>
              <p className="text-[#c47a0a] text-sm font-semibold uppercase tracking-wider mb-4 inline-flex items-center gap-1.5">
                <Palette className="h-3.5 w-3.5" aria-hidden="true" />
                <EditableText
                  editKey="stages.hero.badge"
                  defaultValue="Stages vacances thématiques"
                  as="span"
                  className="text-[#c47a0a] text-sm font-semibold uppercase tracking-wider"
                />
              </p>
              
              <EditableText
                editKey="stages.hero.title"
                defaultValue="Stages Vacances par Thème"
                as="h1"
                className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight tracking-tight"
              />
              
              <EditableText
                editKey="stages.hero.subtitle"
                defaultValue="6 thèmes passionnants ! Choisissez la durée (10h ou 20h) et le thème qui passionne votre enfant : Arduino, Drone, Lego, Makeblock et plus encore."
                as="p"
                multiline
                className="text-xl text-slate-600 mb-8"
              />
              
              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link to="/reserver?type=stage">
                  <Button size="lg" className={`${BTN_PRIMARY} text-lg px-8 py-6`}>
                    <EditableText editKey="stages.hero.cta" defaultValue="Réserver un stage" className="inline" />
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className={`${BTN_OUTLINE} text-lg px-8 py-6`}>
                    <EditableText editKey="stages.hero.ctaSecondary" defaultValue="Nous contacter" className="inline" />
                  </Button>
                </Link>
              </div>

              <div className="grid grid-cols-3 gap-6">
                {[
                  { value: '6', label: 'Thèmes', key: 'stages.hero.stat.1.label' },
                  { value: '10h/20h', label: 'Au choix', key: 'stages.hero.stat.2.label' },
                  { value: '240€', label: 'Dès 240€', key: 'stages.hero.stat.3.label' },
                ].map((stat) => (
                  <div key={stat.key} className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-gradient-brand mb-1">{stat.value}</div>
                    <EditableText
                      editKey={stat.key}
                      defaultValue={stat.label}
                      as="div"
                      className="text-sm text-slate-600"
                    />
                  </div>
                ))}
              </div>
            </div>
            </Reveal>
            
            <Reveal animation="fade-in" delay={120} className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border-2 border-[rgba(58,0,224,0.18)] shadow-2xl shadow-[0_20px_40px_-20px_rgba(58,0,224,0.2)]">
                <img 
                  src={IMAGES.stages.hero} 
                  alt="Stages DigiRobot"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-white/95 backdrop-blur-sm border border-slate-200 rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-2">
                  <IconBadge icon={Shield} variant="orange" size="sm" />
                  <EditableText
                    editKey="stages.hero.floating_badge"
                    defaultValue="Tous niveaux"
                    as="span"
                    className="text-slate-900 font-semibold text-sm"
                  />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <CircuitDivider />

      {/* Video Section */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <SectionHeader
            eyebrow="Vidéo"
            eyebrowKey="stages.video.eyebrow"
            title="Des vacances inoubliables"
            titleKey="stages.video.title"
            subtitle="Découvrez l'ambiance de nos stages vacances"
            subtitleKey="stages.video.subtitle"
            reveal
          />
          
          <Reveal animation="scale-in">
          <div className="aspect-video rounded-2xl overflow-hidden border-2 border-[rgba(58,0,224,0.18)] shadow-2xl relative group cursor-pointer card-hover-glow">
            <img 
              src={IMAGES.workshops.videoPreview} 
              alt="Vidéo stage"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/35 group-hover:bg-black/45 transition-all flex items-center justify-center">
              <div className="w-24 h-24 bg-gradient-to-r from-[#3a00e0] to-[#fab03a] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform shadow-lg shadow-[0_12px_30px_-10px_rgba(58,0,224,0.45)]">
                <Play className="h-12 w-12 text-white ml-2" aria-hidden="true" />
              </div>
            </div>
          </div>
          </Reveal>
        </div>
      </section>

      {/* Choix de la durée */}
      <section id="formules" className="py-12 px-4 scroll-mt-28">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            eyebrow="Formules"
            eyebrowKey="stages.formulas.eyebrow"
            title="Choisissez votre formule"
            titleKey="stages.formulas.title"
            reveal
          />
          
          <div className="grid md:grid-cols-3 gap-6">
            {STAGE_FORMULAS.map((formula) => {
              const reserveUrl = stageFormulaReserveUrl(formula.id);
              return (
              <Card
                key={formula.id}
                role="link"
                tabIndex={0}
                aria-label={`Réserver ${formula.title}`}
                className={`relative cursor-pointer transition-all duration-300 ${
                  selectedDuration === formula.id
                    ? 'border-[#3a00e0] ring-2 ring-[rgba(58,0,224,0.2)] bg-[rgba(58,0,224,0.04)]'
                    : 'border-slate-200 bg-white shadow-sm hover:border-[rgba(58,0,224,0.28)]'
                }`}
                onClick={() => selectAndReserve(formula.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectAndReserve(formula.id);
                  }
                }}
              >
                {formula.popular ? (
                  <div className="absolute top-4 right-4 z-10">
                    <Badge className="bg-[#3a00e0] text-white border-0">
                      <EditableText editKey="stages.formulas.popular_badge" defaultValue="POPULAIRE" as="span" />
                    </Badge>
                  </div>
                ) : null}
                <CardContent className="pt-6 pb-6 flex flex-col h-full">
                  <div className="flex items-center justify-between mb-4">
                    <IconBadge icon={Clock} variant="purple" size="md" />
                    <div className="text-right">
                      <div className="text-3xl font-bold text-slate-900">{formula.price}€</div>
                      <div className="text-sm text-slate-600">TTC</div>
                    </div>
                  </div>
                  <EditableText
                    editKey={formula.titleKey}
                    defaultValue={formula.title}
                    as="h3"
                    className="text-xl font-bold text-slate-900 mb-2"
                  />
                  <EditableText
                    editKey={formula.textKey}
                    defaultValue={formula.text}
                    as="p"
                    className="text-slate-600 text-sm mb-4"
                  />
                  <div className="space-y-2 mb-6 flex-1">
                    {formula.points.map((point) => (
                      <div key={point.key} className="flex items-center text-sm text-slate-600">
                        <CheckCircle2 className="h-4 w-4 text-[#3a00e0] mr-2 shrink-0" />
                        <EditableText editKey={point.key} defaultValue={point.text} as="span" />
                      </div>
                    ))}
                  </div>
                  <Button
                    asChild
                    size="lg"
                    className={`w-full ${BTN_PRIMARY}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Link to={reserveUrl} onClick={() => setSelectedDuration(formula.id)}>
                      <CtaLabel editKey="stages.formulas.cta" defaultValue="Réserver cette formule" />
                      <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Les 6 thèmes */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Thématiques"
            eyebrowKey="stages.themes.eyebrow"
            title="6 thèmes passionnants"
            titleKey="stages.themes.title"
            subtitle="Chaque thème disponible en demi-journée, journée ou semaine"
            subtitleKey="stages.themes.subtitle"
            reveal
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {camps.map((camp, index) => {
              const priceOption = camp.priceOptions?.find((o) => o.duration === selectedDuration)
                || camp.priceOptions?.[0];
              const displayPrice = priceOption?.price ?? 240;

              return (
                <Reveal key={camp.id} animation="fade-up" delay={index * 80}>
                <Card
                  className={`relative overflow-hidden bg-white border-slate-200 shadow-sm card-hover-glow h-full ${
                    camp.robotOption?.available ? 'ring-1 ring-blue-500/20' : ''
                  }`}
                >
                  {camp.robotOption?.available && (
                    <div className="absolute top-4 right-4 z-10">
                      <Badge className="bg-[#18449e] text-white border-0">
                        <EditableText editKey="stages.themes.robot_badge" defaultValue="+ Robot" as="span" />
                      </Badge>
                    </div>
                  )}

                  <div className="h-48 overflow-hidden">
                    <img
                      src={camp.image}
                      alt={camp.title}
                      className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    />
                  </div>

                  <CardHeader className="pb-2">
                    <Badge variant="outline" className="border-[rgba(58,0,224,0.25)] text-[#3a00e0] bg-[rgba(58,0,224,0.05)] w-fit mb-2">
                      {camp.ageRange}
                    </Badge>
                    <CardTitle className="text-slate-900 text-xl">{camp.title}</CardTitle>
                    {camp.sessions?.length > 0 ? (
                      <div className="mt-2">
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
                          {`${camp.sessions.length} session${camp.sessions.length > 1 ? 's' : ''} ouverte${camp.sessions.length > 1 ? 's' : ''}`}
                        </p>
                        <ul
                          className={`space-y-1.5 ${
                            camp.sessions.length > 4
                              ? 'max-h-40 overflow-y-auto pr-1 scrollbar-thin'
                              : ''
                          }`}
                        >
                          {camp.sessions.map((session) => (
                            <li
                              key={session.key}
                              className="text-sm text-slate-700 leading-snug rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-1.5"
                            >
                              <span className="font-medium text-[#3a00e0] capitalize">
                                {session.dateLabel}
                              </span>
                              {session.daysLabel ? (
                                <span className="text-slate-600"> · {session.daysLabel}</span>
                              ) : null}
                              {session.timeLabel ? (
                                <span className="text-slate-600 tabular-nums"> · {session.timeLabel}</span>
                              ) : null}
                              {session.center_name ? (
                                <span className="text-slate-500"> · {session.center_name}</span>
                              ) : null}
                              <span className="text-slate-500">
                                {' · '}
                                {session.available_spots > 0
                                  ? `${session.available_spots} place${session.available_spots > 1 ? 's' : ''}`
                                  : 'Complet'}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : (
                      <div className="mt-1 space-y-1">
                        <p className="text-sm text-slate-500">Prochaines dates à venir</p>
                        <p className="text-sm">
                          <a
                            href="#formules"
                            className="text-[#3a00e0] font-medium hover:underline"
                          >
                            Voir les formules
                          </a>
                          <span className="text-slate-400"> · </span>
                          <Link
                            to={stageFormulaReserveUrl(selectedDuration)}
                            className="text-[#3a00e0] font-medium hover:underline"
                          >
                            Réserver une formule avec dates
                          </Link>
                        </p>
                      </div>
                    )}
                    <CardDescription className="text-slate-600 line-clamp-2">
                      {camp.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      {camp.included.slice(0, 3).map((item, i) => (
                        <div key={i} className="flex items-start text-sm text-slate-600">
                          <CheckCircle2 className="h-4 w-4 text-[#3a00e0] mr-2 mt-0.5 shrink-0" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>

                    {camp.robotOption?.available && (
                      <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                        <div className="flex items-center mb-1.5">
                          <Package className="h-4 w-4 text-blue-400 mr-2" />
                          <EditableText
                            editKey="stages.themes.robot_option_label"
                            defaultValue="Option Robot"
                            as="span"
                            className="text-blue-400 font-medium text-sm"
                          />
                        </div>
                        {camp.robotOption.robots.slice(0, 2).map((robot, i) => (
                          <div key={i} className="text-xs text-slate-600">
                            {robot.name} : +{robot.price}€ (val. {robot.value}€)
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="pt-2 border-t border-slate-200">
                      <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">
                        {selectedDuration === 'semaine'
                          ? 'Semaine complète'
                          : selectedDuration === 'journee'
                            ? 'Journée'
                            : 'Demi-journée'}
                      </div>
                      <div className="flex items-baseline gap-2 mb-4">
                        <span className="text-3xl font-bold text-slate-900">{displayPrice}€</span>
                        <span className="text-sm text-slate-600">TTC</span>
                      </div>
                      <Link
                        to={
                          camp.has_open_slots
                            ? `/reserver?type=stage&offer=${STAGE_OFFER_SLUG}&duration=${selectedDuration}&stage=${camp.id}`
                            : `/reserver?type=stage&offer=${STAGE_OFFER_SLUG}&duration=${selectedDuration}`
                        }
                        className="block"
                      >
                        <Button className={`w-full ${BTN_PRIMARY}`}>
                          <EditableText editKey="stages.card.cta" defaultValue="Réserver" as="span" />
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Parcours"
            eyebrowKey="stages.how.eyebrow"
            title="Comment se déroule un stage ?"
            titleKey="stages.how.title"
            subtitle="5 jours pour devenir un expert !"
            subtitleKey="stages.how.subtitle"
            reveal
          />
          <StepCards steps={STAGE_STEPS} columns={3} keyPrefix="stages.how" />
        </div>
      </section>

      {/* Compétences */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Compétences"
            eyebrowKey="stages.skills.eyebrow"
            title="Compétences développées pendant le stage"
            titleKey="stages.skills.title"
            subtitle="Bien plus que de la technique !"
            subtitleKey="stages.skills.subtitle"
            reveal
          />

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {STAGE_SKILLS.map((skill, i) => (
              <Reveal key={skill.title} animation="fade-up" delay={i * 60}>
                <FeatureCard
                  {...skill}
                  size="lg"
                  titleKey={`stages.skills.${i + 1}.title`}
                  descriptionKey={`stages.skills.${i + 1}.text`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi choisir nos stages */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <SectionHeader
            eyebrow="Nos atouts"
            eyebrowKey="stages.why.eyebrow"
            title="Pourquoi choisir nos stages vacances ?"
            titleKey="stages.why.title"
            reveal
          />

          <div className="grid md:grid-cols-3 gap-6">
            {STAGE_BENEFITS.map((item, i) => (
              <Reveal key={item.title} animation="fade-up" delay={i * 80}>
                <FeatureCard
                  {...item}
                  centered={false}
                  titleKey={`stages.why.${i + 1}.title`}
                  descriptionKey={`stages.why.${i + 1}.text`}
                />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <TestimonialsSection
        testimonials={testimonials}
        title="Retours de parents"
        titleKey="stages.testimonials.title"
        eyebrowKey="stages.testimonials.eyebrow"
        ratingKey="stages.testimonials.rating"
        bgClassName=""
      />

      {/* FAQ */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            eyebrow="FAQ"
            eyebrowKey="stages.faq.eyebrow"
            title="Questions fréquentes"
            titleKey="stages.faq.title"
            subtitle="Tout ce que vous devez savoir sur nos stages"
            subtitleKey="stages.faq.subtitle"
            reveal
          />

          <EditableFaqList items={stagesFAQ} keyPrefix="stages.faq" />
        </div>
      </section>

      <CtaBanner
        title="Offrez des vacances inoubliables à votre enfant !"
        titleKey="stages.cta.title"
        subtitle="Réservez maintenant sa place pour les prochaines vacances. 6 thèmes disponibles en 10h ou 20h."
        subtitleKey="stages.cta.subtitle"
        footer="Matériel fourni · Petits groupes · Tous niveaux acceptés"
        footerKey="stages.cta.footer"
      >
        <Link to="/reserver?type=stage">
          <Button size="lg" className="bg-white text-[#3a00e0] hover:bg-[rgba(58,0,224,0.06)] text-xl px-12 py-8">
            <EditableText editKey="stages.cta.button" defaultValue="Réserver un stage — Dès 240€" className="inline" />
            <ArrowRight className="ml-2 h-6 w-6" />
          </Button>
        </Link>
      </CtaBanner>

      <Footer />
    </div>
  );
};

export default Stages;
